import React, { useContext, useState, useEffect, useMemo } from "react";
import { productsData } from "../ProductsData";
import { CartContext } from "./CartContext";
import { useWishlist } from "./WishlistContext";
import { Link } from "react-router-dom";
import '../css/ProductCard.css'

const ProductsList = ({ category, subcategory }) => {
    const { addToCart } = useContext(CartContext);
    const { wishlistItems, toggleWishlist } = useWishlist();

    const [alert, setAlert] = useState({ visible: false, message: "", type: "" });
    const [selectedCurrency, setSelectedCurrency] = useState(localStorage.getItem("selectedCurrency") || "USD");

    const conversionRate = 88.79;

    // --- Currency and Scroll Logic ---
    useEffect(() => {
        const handleCurrencyChange = () => {
            const savedCurrency = localStorage.getItem("selectedCurrency") || "USD";
            setSelectedCurrency(savedCurrency);
        };
        window.addEventListener("storage", handleCurrencyChange);
        return () => window.removeEventListener("storage", handleCurrencyChange);
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            const savedCurrency = localStorage.getItem("selectedCurrency") || "USD";
            if (savedCurrency !== selectedCurrency) {
                setSelectedCurrency(savedCurrency);
            }
        }, 500);
        return () => clearInterval(interval);
    }, [selectedCurrency]);

    const convertPrice = (price) => {
        return selectedCurrency === "INR" ? price * conversionRate : price;
    };

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);
    // ---------------------------------------------

    // --- Product Filtering Logic ---
    const products = useMemo(() => {
        let prods = [];
        if (category && subcategory) {
            prods = productsData[category]?.[subcategory] || [];
        } else if (category) {
            const cat = productsData[category];
            if (Array.isArray(cat)) prods = cat;
            else if (typeof cat === "object") {
                prods = Object.entries(cat).flatMap(([sub, arr]) =>
                    arr.map(p => ({ ...p, category, subcategory: sub }))
                );
            }
        } else {
            prods = Object.entries(productsData).flatMap(([catKey, catVal]) =>
                Array.isArray(catVal)
                    ? catVal.map(p => ({ ...p, category: catKey }))
                    : Object.entries(catVal).flatMap(([subKey, arr]) =>
                        arr.map(p => ({ ...p, category: catKey, subcategory: subKey }))
                    )
            );
            prods = prods.sort(() => 0.5 - Math.random()).slice(0, 12);
        }
        return prods;
    }, [category, subcategory]);

    const handleAddToCart = (product) => {
        addToCart(product);
        setAlert({
            visible: true,
            message: `${product.name} added to cart!`,
            type: "success"
        });
        setTimeout(() => setAlert({ visible: false, message: "", type: "" }), 3000);
    };

    // --- RENDER LOGIC ---
    return (
        <div className="container py-5">
            {/* Alert Display */}
            {alert.visible && (
                <div
                    className={`bg-primary text-white alert alert-${alert.type} alert-dismissible fade show fixed-top mt-3 mx-auto`}
                    role="alert"
                    style={{ zIndex: 1050, width: 'fit-content', minWidth: '300px' }}
                >
                    <i className="fa-solid fa-check-circle me-2"></i>
                    {alert.message}
                    <button
                        type="button"
                        className="btn-close"
                        onClick={() => setAlert({ visible: false, message: "", type: "" })}
                    ></button>
                </div>
            )}

            <div className="container py-5 min-vh-100">
                <div className="row g-4">
                    {products.map(product => {
                        const productCategory = category || product.category;
                        const productSubcategory = subcategory || product.subcategory;
                        const productLink = productSubcategory
                            ? `/product/${productCategory}/${productSubcategory}/${product.id}`
                            : `/product/${productCategory}/${product.id}`;
                        const productWithRouteInfo = { ...product, category: productCategory, subcategory: productSubcategory };

                        const isWishlisted = wishlistItems.find(item =>
                            item.id === product.id &&
                            item.category === productCategory &&
                            (!productSubcategory || item.subcategory === productSubcategory)
                        );

                        const price = convertPrice(product.price);
                        const oldPrice = product.oldPrice ? convertPrice(product.oldPrice) : null;
                        const discountPercent = product.oldPrice ? Math.round(((product.oldPrice - product.price) / product.oldPrice) * 100) : 0;

                        return (
                            <div key={product.id} className="col-12 col-sm-6 col-md-4 col-lg-3">
                                {/* New Card Design - Inspired by Nike Best Seller */}
                                <div className="card h-100 border-0 rounded-3 overflow-hidden shadow-sm product-card-hover">

                                    {/* Best Seller Badge */}
                                    <div className="position-absolute top-0 start-0 m-3">
                                        <span className="badge bg-dark text-white px-3 py-2 rounded-pill">
                                            Best Seller
                                        </span>
                                    </div>

                                    {/* Wishlist Button - Top Right */}
                                    <button
                                        className={`btn btn-light position-absolute top-0 end-0 m-3 rounded-circle shadow-sm`}
                                        onClick={(e) => { e.stopPropagation(); toggleWishlist(productWithRouteInfo); }}
                                        style={{ width: '40px', height: '40px', padding: 0, zIndex: 999 }}
                                    >
                                        <i className={`fa-heart fa-sm ${isWishlisted ? 'fa-solid text-danger' : 'fa-regular'}`}></i>
                                    </button>

                                    {/* Product Image */}
                                    <div className="product-image-container">
                                        <Link
                                            to={productLink}
                                            className="d-block w-100 h-100"
                                        >
                                            <img
                                                src={product.image}
                                                className="product-image"
                                                alt={product.name}
                                            />
                                        </Link>
                                    </div>

                                    {/* Product Info */}
                                    <div className="card-body p-4">
                                        {/* Category */}
                                        <p className="text-uppercase text-muted small mb-1 fw-bold">
                                            {productSubcategory ? productSubcategory : productCategory}
                                        </p>

                                        {/* Product Name */}
                                        <Link to={productLink} className="text-decoration-none text-dark">
                                            <h5 className="card-title fw-bold mb-2" style={{ fontSize: '1.1rem', lineHeight: '1.3' }}>
                                                {product.name.slice(0, 30) || 'Product Name'}...
                                            </h5>
                                        </Link>

                                        {/* Description - Short version */}
                                        <p className="text-dark small mb-4" style={{ lineHeight: '1.4', fontSize: '0.85rem' }}>
                                            {product.description.slice(0, 60)}...
                                        </p>

                                        {/* Price and Button Section */}
                                        <div className="d-flex justify-content-between align-items-center mt-4">
                                            {/* Price */}
                                            <div className="d-flex align-items-baseline">
                                                <span className="fw-bold fs-4 text-dark">
                                                    {selectedCurrency === "INR" ? `₹${price.toFixed(0)}` : `$${price.toFixed(0)}`}
                                                </span>
                                                {oldPrice && (
                                                    <span className="text-muted text-decoration-line-through ms-2 small">
                                                        {selectedCurrency === "INR" ? `₹${oldPrice.toFixed(0)}` : `$${oldPrice.toFixed(0)}`}
                                                    </span>
                                                )}
                                            </div>

                                            {/* Buy Now Button */}
                                            <button
                                                className="btn btn-dark rounded-pill px-4 py-2 fw-bold buy-now-btn"
                                                onClick={() => handleAddToCart(product)}
                                                style={{
                                                    fontSize: '0.9rem',
                                                    transition: 'all 0.3s ease'
                                                }}
                                            >
                                                Add to Cart <i class="fa-solid fa-arrow-up-right-from-square"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {products.length === 0 && (
                <p className="text-center text-muted mt-4">No products found.</p>
            )}
        </div>
    );
};

export default ProductsList;